import React, { useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Helmet } from 'react-helmet';
import api from '../services/apiService';
import ActivityForm from './ActivityForm';

const toDateKey = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthRange = date => ({
  dateFrom: toDateKey(new Date(date.getFullYear(), date.getMonth(), 1)),
  dateTo: toDateKey(new Date(date.getFullYear(), date.getMonth() + 1, 0)),
});

const formatTime = value => (
  value ? String(value).slice(0, 5) : ''
);

const getAccountName = activity => (
  activity.customer?.company
  || activity.prospect?.company
  || activity.pipelineInstance?.name
  || 'Ohne Zuordnung'
);

const getContactName = activity => {
  const contact = activity.prospectContact || activity.contact;
  if (!contact) return '';

  return [
    contact.contactFirstName || contact.firstName,
    contact.contactLastName || contact.lastName,
  ].filter(Boolean).join(' ');
};

const sortFollowUps = (left, right) => {
  const leftKey = `${left.followUpDate || ''} ${left.followUpTime || ''}`;
  const rightKey = `${right.followUpDate || ''} ${right.followUpTime || ''}`;
  return leftKey.localeCompare(rightKey) || Number(left.id) - Number(right.id);
};

function ActivityModal({ activity, onClose, onSaved, onDeleted }) {
  if (!activity) return null;

  return (
    <div
      className="crm-follow-up-modal-backdrop"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="crm-follow-up-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Aktivität ${activity.id} bearbeiten`}
      >
        <button
          type="button"
          className="crm-follow-up-modal-close"
          onClick={onClose}
          aria-label="Schließen"
        >
          ×
        </button>
        <ActivityForm
          focusActivityId={activity.id}
          pipelineInstId={activity.pipelineInstId}
          customer={activity.customer}
          contact={activity.contact}
          prospect={activity.prospect}
          prospectContact={activity.prospectContact}
          showAddRow={false}
          showFilters={false}
          disableFilterPersistence
          onActivitySaved={onSaved}
          onActivityDeleted={onDeleted}
          onEditCancel={onClose}
        />
      </div>
    </div>
  );
}

const CrmFollowUpCalendar = () => {
  const [activeDate, setActiveDate] = useState(new Date());
  const [followUps, setFollowUps] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const range = useMemo(() => getMonthRange(activeDate), [activeDate]);

  useEffect(() => {
    let cancelled = false;

    const loadFollowUps = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await api.get('/activities/follow-ups', {
          params: range,
        });
        if (!cancelled) {
          setFollowUps(
            (Array.isArray(response.data?.rows) ? response.data.rows : [])
              .sort(sortFollowUps)
          );
        }
      } catch (requestError) {
        if (!cancelled) {
          setFollowUps([]);
          setError(
            requestError.response?.data?.error
            || 'Die Wiedervorlagen konnten nicht geladen werden.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFollowUps();
    return () => {
      cancelled = true;
    };
  }, [range]);

  const followUpsByDate = useMemo(() => {
    const grouped = new Map();

    for (const activity of followUps) {
      if (!activity.followUpDate) continue;
      const entries = grouped.get(activity.followUpDate) || [];
      entries.push(activity);
      grouped.set(activity.followUpDate, entries);
    }

    return grouped;
  }, [followUps]);

  const updateActivity = savedActivity => {
    setSelectedActivity(savedActivity);
    setFollowUps(previous => {
      const remaining = previous.filter(
        activity => activity.id !== savedActivity.id
      );
      const belongsToVisibleMonth = (
        savedActivity.followUpDate
        && savedActivity.followUpDate >= range.dateFrom
        && savedActivity.followUpDate <= range.dateTo
      );

      return (
        belongsToVisibleMonth
          ? [...remaining, savedActivity]
          : remaining
      ).sort(sortFollowUps);
    });
  };

  const deleteActivity = activityId => {
    setFollowUps(previous => previous.filter(
      activity => activity.id !== activityId
    ));
    setSelectedActivity(null);
  };

  return (
    <div className="crm-follow-up-page">
      <Helmet>
        <title>CRM-Wiedervorlagen</title>
        <meta
          name="description"
          content="Kalenderübersicht der CRM-Wiedervorlagen"
        />
      </Helmet>

      <div className="crm-follow-up-heading">
        <div>
          <h2>CRM-Wiedervorlagen</h2>
          <p>
            Ein Klick auf eine Wiedervorlage öffnet die zugehörige Aktivität.
          </p>
        </div>
        <div className="crm-follow-up-heading-actions">
          <button
            type="button"
            className="crm-follow-up-today-button"
            onClick={() => setActiveDate(new Date())}
          >
            Heute
          </button>
          <div className="crm-follow-up-legend">
            <span><i className="is-open" /> offen</span>
            <span><i className="is-done" /> erledigt</span>
          </div>
        </div>
      </div>

      {error && <div className="crm-follow-up-error">{error}</div>}

      <div className="crm-follow-up-calendar-wrap">
        <Calendar
          value={null}
          activeStartDate={new Date(
            activeDate.getFullYear(),
            activeDate.getMonth(),
            1
          )}
          locale="de-DE"
          minDetail="month"
          maxDetail="month"
          showWeekNumbers
          showNeighboringMonth={false}
          prev2Label={null}
          next2Label={null}
          navigationLabel={({ date }) => (
            date.toLocaleDateString('de-DE', {
              month: 'long',
              year: 'numeric',
            })
          )}
          formatShortWeekday={(_, date) => (
            date.toLocaleDateString('de-DE', { weekday: 'short' })
              .replace('.', '')
          )}
          onActiveStartDateChange={({ activeStartDate }) => {
            if (activeStartDate) setActiveDate(activeStartDate);
          }}
          tileContent={({ date, view }) => {
            if (view !== 'month') return null;
            const entries = followUpsByDate.get(toDateKey(date)) || [];

            return (
              <div className="crm-follow-up-day-entries">
                {entries.map(activity => {
                  const accountName = getAccountName(activity);
                  const contactName = getContactName(activity);
                  const time = formatTime(activity.followUpTime);
                  const title = [
                    time ? `${time} Uhr` : null,
                    accountName,
                    contactName,
                    activity.description,
                  ].filter(Boolean).join(' · ');

                  return (
                    <div
                      key={activity.id}
                      role="button"
                      tabIndex={0}
                      className={`crm-follow-up-entry ${
                        activity.status === 'erledigt' ? 'is-done' : 'is-open'
                      }`}
                      title={title}
                      onClick={event => {
                        event.stopPropagation();
                        setSelectedActivity(activity);
                      }}
                      onKeyDown={event => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        event.stopPropagation();
                        setSelectedActivity(activity);
                      }}
                    >
                      {time && <strong>{time}</strong>}
                      <span>{accountName}</span>
                    </div>
                  );
                })}
              </div>
            );
          }}
        />
        {loading && (
          <div className="crm-follow-up-loading">Wiedervorlagen werden geladen …</div>
        )}
      </div>

      {!loading && !error && followUps.length === 0 && (
        <div className="crm-follow-up-empty">
          In diesem Monat sind keine Wiedervorlagen eingetragen.
        </div>
      )}

      <ActivityModal
        activity={selectedActivity}
        onClose={() => setSelectedActivity(null)}
        onSaved={updateActivity}
        onDeleted={deleteActivity}
      />
    </div>
  );
};

export default CrmFollowUpCalendar;
